import Divider from '@mui/material/Divider';
import {
  FieldValues, FormProvider, useForm, useWatch,
} from 'react-hook-form';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import {
  Fragment, useEffect, useMemo, useRef,
} from 'react';
import AppBar from '@mui/material/AppBar';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import DOMPurify from 'dompurify';
import Stack from '@mui/material/Stack';
import ReactQuill from 'react-quill';
import startCase from 'lodash/startCase';
import moment from 'moment-timezone';
import Grid from '@mui/material/Grid2';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { Iconify } from '@mgm/common/components';
import { BaseDrawer, BaseDrawerTitle, MemoizedBaseAvatar } from '@mgm/common/components/base';
import { BASE_COLORS, dateFormatTZ, isSameOrAfter } from '@mgm/common/utils';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import {
  getIsOpenShiftNotesNav,
  getSelectedScheduleShift,
} from '../../../../store/schedules/selectors';
import { schedulesActions } from '../../../../store/schedules/slice';
import { TScheduleShift } from '../../../../types';
import RHFEditor from '../../../../components/editor/RHFEditor';
import { useDeleteNoteAPI, useUpsertNoteAPI } from '../../../../hooks/api/useSchedules';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { commonActions } from '../../../../store/common/slice';

const ShiftNotesNav = () => {
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const isOpenShiftNotesNav = useAppSelector(getIsOpenShiftNotesNav);
  const { t } = useTranslation(['tasklists']);
  const dispatch = useAppDispatch();
  const quillRef = useRef<ReactQuill | null>(null);
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { mutateAsync: upsertNote, isLoading: isUpsertNoteLoading } = useUpsertNoteAPI();
  const { mutateAsync: deleteNote, isLoading: isDeleteNoteLoading } = useDeleteNoteAPI();

  const selectedScheduleShift = useAppSelector(getSelectedScheduleShift) as TScheduleShift;

  const methods = useForm({
    mode: 'all',
  });

  const noteValue = useWatch({
    control: methods.control,
    name: 'note',
    defaultValue: '',
  });

  useEffect(() => {
    if (isOpenShiftNotesNav) {
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        editor.enable();
      }
    }
  }, [isOpenShiftNotesNav]);

  const handleCloseNav = () => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      editor.disable();
    }
    dispatch(schedulesActions.setIsOpenShiftNotesNav(false));
  };

  const addNote = async (payload: FieldValues) => {
    const data = {
      note: payload.note,
      scheduleId: selectedScheduleShift.id as number,
    };

    await upsertNote(data);
    methods.resetField('note');
    handleCloseNav();
  };

  const title = useMemo(() => {
    const start = dateFormatTZ(selectedScheduleShift.start, 'h:mm A', selectedScheduleShift.timezone);
    const end = dateFormatTZ(selectedScheduleShift.end, 'h:mm A', selectedScheduleShift.timezone);

    return `${selectedScheduleShift.name} -- ${start} - ${end}`;
  }, [selectedScheduleShift]);

  const handleDeleteNote = async () => {
    const data = {
      scheduleId: selectedScheduleShift.id as number,
    };
    await deleteNote(data);
    handleCloseNav();
  };

  const onCloseDrawer = () => {
    dispatch(commonActions.setToggleDrawer(false));
  };

  return (
    <BaseDrawer
      drawerProps={{
        anchor: 'right',
        width: isMobile ? 'auto' : 650,
        variant: 'persistent',
      }}
      open={isOpenShiftNotesNav}
      onCloseDrawer={onCloseDrawer}
    >
      <BaseDrawerTitle handleClose={handleCloseNav} title={title} subtitle={selectedScheduleShift.location?.name} />
      <Divider sx={{ borderStyle: 'dashed' }} />
      <FormProvider {...methods}>
        <Box
          paddingRight={0}
          paddingLeft={0}
          component="form"
          onSubmit={methods.handleSubmit(addNote)}
        >
          <List sx={{
            width: '100%', height: 500, overflow: 'auto', bgcolor: 'background.paper',
          }}
          >
            {selectedScheduleShift.notes?.map((note) => (
              <Fragment key={note.note}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <ListItem
                    alignItems="flex-start"
                  >
                    <ListItemAvatar>
                      <MemoizedBaseAvatar avatarName={note.creator?.name} avatarFile={note.creator?.avatarFile} />
                    </ListItemAvatar>
                    <Grid
                      sx={{
                        width: '100%',
                      }}
                      container
                    >
                      <Grid
                        size={{
                          xs: 12, md: 6, sm: 6, lg: 6,
                        }}
                        {...isMobile && {
                          pb: 2,
                        }}
                      >
                        <Stack>
                          <Typography
                            pr={2}
                            component="span"
                            variant="caption"
                            sx={{ color: 'text.primary', display: 'inline' }}
                          >
                            {startCase(note.creator?.name)}
                          </Typography>
                          <Typography
                            pr={2}
                            component="span"
                            variant="caption"
                            color="text.disabled"
                            sx={{ display: 'inline' }}
                          >
                            {moment(note.createdAt).format('DD MMMM, hh:mm A')}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid size={{
                        xs: 12, md: 6, sm: 6, lg: 6,
                      }}
                      >
                        <ListItemText
                          secondary={(
                            <Typography
                              component="span"
                              variant="body2"
                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.note) }}
                              sx={{ color: 'text.primary', display: 'inline' }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </ListItem>
                  {note.creator?.email === personnel.email && isSameOrAfter(selectedScheduleShift.start) && (
                  <Box
                    sx={{
                      cursor: 'pointer',
                    }}
                    pr={2}
                    onClick={handleDeleteNote}
                  >
                    <Iconify
                      sx={{
                        color: BASE_COLORS.red,
                      }}
                      icon="material-symbols-light:delete-outline-rounded"
                      width={30}
                    />
                  </Box>
                  )}
                </Stack>
                <Divider sx={{ borderStyle: 'dashed', mt: 0.5 }} />
              </Fragment>
            ))}
          </List>
          {isSameOrAfter(selectedScheduleShift.start) && (
          <AppBar position="fixed" color="transparent" sx={{ top: 'auto', bottom: 0 }}>
            <RHFEditor quillRef={quillRef as unknown as ReactQuill | null} simple name="note" />
            <LoadingButton
              aria-label="add note"
              loading={isUpsertNoteLoading}
              disabled={!noteValue.trim() || isDeleteNoteLoading}
              type="submit"
              sx={{ flexGrow: 1 }}
            >
              {t('addNote')}
            </LoadingButton>
          </AppBar>
          )}
        </Box>
      </FormProvider>
    </BaseDrawer>
  );
};

export {
  ShiftNotesNav,
};
